import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SALES_CHANNELS } from '@bre-ticketing/contracts';
import type { ApiError } from '../api';
import { addOnEndpoints, eventEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Panel, Table } from '../ui';
import { INPUT, dollars, humanize } from './event/shared';

/**
 * The add-on catalog (item 49 — D-314): parking, a voucher, a poster.
 *
 * Two things on this screen are the ones somebody actually comes for: **how many
 * are left**, and **how many sold**. The first decides whether to raise a limit
 * tonight; the second decides whether to run it again next season. Everything
 * else is a form.
 *
 * There is no delete. `active = 0` is what "stop offering it" means, and a
 * retired product keeps its sold count — which is the number the second question
 * needs.
 */
export default function AddOnsPage() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const addOns = useQuery({ queryKey: ['add-ons'], queryFn: addOnEndpoints.list });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['add-ons'] });
  const onError = (cause: ApiError) => setError(cause.message);

  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) => addOnEndpoints.update(id, input),
    onSuccess: () => {
      setError(null);
      void refresh();
    },
    onError,
  });

  if (!can('events.view')) return <NotPermitted permission="events.view" />;
  const editable = can('add_ons.manage');
  const rows = addOns.data?.add_ons ?? [];

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}

      {adding ? <NewAddOn onDone={() => { setAdding(false); void refresh(); }} onCancel={() => setAdding(false)} onError={onError} /> : null}

      <Panel
        title="Extras"
        description="Things a customer can buy beside a ticket. An extra with no event is offered on everything; one with an event is offered only on that event. They appear on the order and on the will-call sheet — nothing here redeems them, so a voucher is honored by somebody reading it."
        actions={
          editable && !adding ? (
            <Button variant="primary" onClick={() => setAdding(true)}>
              Add an extra
            </Button>
          ) : null
        }
      >
        <Table headers={['Extra', 'Price', 'Where', 'Left', 'Sold', '']}>
          {addOns.isPending ? (
            <EmptyRow columns={6}>Loading…</EmptyRow>
          ) : rows.length === 0 ? (
            <EmptyRow columns={6}>Nothing yet. A parking pass or a drink voucher is the usual first one.</EmptyRow>
          ) : (
            rows.map((row) => (
              <tr key={row.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3">
                  <div className="font-medium text-ink-900">
                    {row.name}
                    {row.active === 0 ? <Badge tone="neutral">off</Badge> : null}
                  </div>
                  <div className="text-xs text-ink-400">
                    {row.code}
                    {row.event_id ? ' · one event only' : ' · every event'}
                    {` · up to ${row.max_per_order} per order`}
                  </div>
                  {row.description ? <div className="mt-0.5 max-w-md text-xs text-ink-500">{row.description}</div> : null}
                </td>
                <td className="tabular px-5 py-3 text-sm">{dollars(row.price_cents)}</td>
                <td className="px-5 py-3 text-xs text-ink-600">{row.channels.map((channel) => humanize(channel)).join(', ')}</td>
                <td className="tabular px-5 py-3 text-sm">
                  {/* Unlimited says so, rather than showing a blank that reads as nought. */}
                  {row.stock_limit === null ? <span className="text-xs text-ink-400">unlimited</span> : Math.max(0, row.stock_limit - row.sold)}
                </td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">{row.sold}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  {editable ? (
                    <Button disabled={update.isPending} onClick={() => update.mutate({ id: row.id, input: { active: row.active === 0 } })}>
                      {row.active === 1 ? 'Stop offering' : 'Offer again'}
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}

function NewAddOn({ onDone, onCancel, onError }: { onDone: () => void; onCancel: () => void; onError: (error: ApiError) => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [eventId, setEventId] = useState('');
  const [maxPerOrder, setMaxPerOrder] = useState('10');
  const [stockLimit, setStockLimit] = useState('');
  const [channels, setChannels] = useState<string[]>(['WEB']);
  const events = useQuery({ queryKey: ['events-list', 'PUBLISHED'], queryFn: () => eventEndpoints.list('PUBLISHED') });

  const create = useMutation({
    mutationFn: () =>
      addOnEndpoints.create({
        code: code.trim().toUpperCase(),
        name: name.trim(),
        description: description.trim() || null,
        price_cents: Math.round(Number(price) * 100),
        event_id: eventId || null,
        max_per_order: Number(maxPerOrder) || 10,
        // Blank means unlimited, which is a different thing from nought.
        stock_limit: stockLimit.trim() === '' ? null : Number(stockLimit),
        channels,
        active: true,
        sort_order: 0,
      }),
    onSuccess: onDone,
    onError,
  });

  const ready = code.trim().length > 0 && name.trim().length > 0 && price.trim() !== '' && channels.length > 0;

  return (
    <Panel title="A new extra" description="The price is set here and nowhere else: a page can never name its own amount.">
      <form
        className="flex flex-wrap items-end gap-4 px-5 py-4"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <Labeled label="Code" hint="capitals and underscores">
          <input value={code} onChange={(event) => setCode(event.target.value.toUpperCase())} placeholder="PARKING" className={`${INPUT} w-40`} />
        </Labeled>
        <Labeled label="Name" hint="what the customer reads">
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Parking pass" className={`${INPUT} w-56`} />
        </Labeled>
        <Labeled label="Description" hint="one line, optional">
          <input value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Lot B, opens an hour before" className={`${INPUT} w-72`} />
        </Labeled>
        <Labeled label="Price" hint="dollars">
          <input value={price} onChange={(event) => setPrice(event.target.value)} inputMode="decimal" placeholder="10.00" className={`${INPUT} w-24`} />
        </Labeled>
        <Labeled label="Event" hint="blank offers it on everything">
          <select value={eventId} onChange={(event) => setEventId(event.target.value)} className={INPUT}>
            <option value="">Every event</option>
            {(events.data?.events ?? []).map((event: { id: string; title: string }) => (
              <option key={event.id} value={event.id}>
                {event.title}
              </option>
            ))}
          </select>
        </Labeled>
        <Labeled label="Per order" hint="most one customer may buy">
          <input value={maxPerOrder} onChange={(event) => setMaxPerOrder(event.target.value)} inputMode="numeric" className={`${INPUT} w-20`} />
        </Labeled>
        <Labeled label="How many exist" hint="blank means unlimited">
          <input value={stockLimit} onChange={(event) => setStockLimit(event.target.value)} inputMode="numeric" placeholder="unlimited" className={`${INPUT} w-28`} />
        </Labeled>
        <div>
          <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Sold where</span>
          <div className="mt-1 flex flex-wrap gap-3">
            {SALES_CHANNELS.map((channel) => (
              <label key={channel} className="flex items-center gap-1 text-sm text-ink-700">
                <input
                  type="checkbox"
                  checked={channels.includes(channel)}
                  onChange={(event) => setChannels(event.target.checked ? [...channels, channel] : channels.filter((existing) => existing !== channel))}
                />
                {humanize(channel)}
              </label>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={!ready || create.isPending}>
            {create.isPending ? 'Adding…' : 'Add it'}
          </Button>
          <Button type="button" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function Labeled({ label, hint, children }: { label: string; hint: string; children: React.ReactNode }) {
  return (
    <label className="text-sm">
      <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</span>
      <span className="mt-1 block">{children}</span>
      <span className="mt-0.5 block text-xs text-ink-400">{hint}</span>
    </label>
  );
}
